import { useState, type ReactNode } from "react";

import { useAlCambiar, useDatos } from "../api";
import { Carga } from "../componentes/Carga";
import { Badge, BarChart, Button, Card, DataTable, EmptyState, Heading, MetricCard, Page, Progress, SegmentedControl, StatRow, Text, TopBar, type DataTableColumn } from "../ds";
import { fecha, fechaHora, motivoDescarte, nombrePlantilla, numero, porcentaje, proveedor, TZ, usd } from "../formato";
import { Enlace } from "../router";
import { useSesion } from "../sesion";
import type { Embudo, MetricasEmbudo, RendimientoPlantilla, RendimientoSeguimiento, Resumen } from "../tipos";
import s from "./Tablero.module.css";

export function Tablero() {
  const [dias, setDias] = useState<"7" | "30" | "90">("30");
  const { datos: r, error, recargar } = useDatos<Resumen>(`/api/resumen?dias=${dias}`);
  const { sesion } = useSesion();
  useAlCambiar(recargar);

  const sheets = sesion?.integraciones.sheets.url;
  return (
    <>
      <TopBar
        title="Tablero"
        subtitle={r?.ultimaCorrida ? `Cómo viene la prospección en el AMBA. Última búsqueda el ${fechaHora(r.ultimaCorrida)}.` : "Cómo viene la prospección en el AMBA."}
        actions={
          sheets ? (
            <Button size="sm" variant="outline" iconAfter="external-link" href={sheets} target="_blank">
              Abrir planilla
            </Button>
          ) : null
        }
      />
      <Page width="none">
        {!r ? (
          <Carga error={error} onReintentar={recargar} filas={10} />
        ) : (
          <Contenido r={r} dias={dias} setDias={setDias} />
        )}
      </Page>
    </>
  );
}

function Contenido({ r, dias, setDias }: { r: Resumen; dias: "7" | "30" | "90"; setDias: (d: "7" | "30" | "90") => void }) {
  const e = r.estados;
  const calificadas = (e.calificada ?? 0) + (e.contactada ?? 0) + (e.respondio ?? 0) + (e.reunion ?? 0) + (e.cliente ?? 0);
  const embudo = [
    { etiqueta: "Calificadas", n: calificadas },
    { etiqueta: "Contactadas", n: (e.contactada ?? 0) + (e.respondio ?? 0) + (e.reunion ?? 0) + (e.cliente ?? 0) },
    { etiqueta: "Respondieron", n: (e.respondio ?? 0) + (e.reunion ?? 0) + (e.cliente ?? 0) },
    { etiqueta: "Reunión", n: (e.reunion ?? 0) + (e.cliente ?? 0) },
    { etiqueta: "Clientes", n: e.cliente ?? 0 },
  ];
  const descartadas = e.descartada ?? 0;
  const bajas = e.baja ?? 0;
  const maxHist = Math.max(1, ...r.histograma.map((h) => h.n));
  const maxZona = Math.max(1, ...r.zonas.top.map((z) => z.n));
  const sitiosLeidos = r.proveedores.reduce((a, p) => a + p.n, 0);
  const maxProv = Math.max(1, ...r.proveedores.map((p) => p.n));
  // Los detectados primero; "Sin detectar" al final aunque sea el más grande.
  const proveedores = [...r.proveedores].sort((a, b) => Number(a.proveedor === null) - Number(b.proveedor === null) || b.n - a.n);
  const tokko = r.proveedores.find((p) => p.proveedor === "tokko")?.n ?? 0;
  const hayActividad = r.serie.some((x) => x.enviados || x.respuestas || x.autorespuestas);

  return (
    <>
      <div className={s.metricas}>
        <MetricCard label="Inmobiliarias" value={numero(r.total)} icon="building-2" period={`En ${r.zonas.recorridas} de las ${r.zonas.total} zonas del AMBA`} />
        <MetricCard label="Calificadas" value={numero(calificadas)} icon="check-circle-2" period={`El ${porcentaje(calificadas, r.total)} de las encontradas entra en el perfil`} />
        <MetricCard label="Listas para WhatsApp" value={numero(r.listasWhatsapp)} icon="whatsapp" period={`Con móvil, puntaje de ${r.minimoWhatsapp} o más y sin borrador`} />
        <MetricCard label="Contactadas" value={numero(r.contactadas)} icon="send" period={r.contactadas ? "Con al menos un mensaje enviado" : "Todavía no salió ningún mensaje"} />
      </div>

      <div className={s.tres}>
        <Card padding="md" radius="panel">
          <Cabecera titulo="Embudo" nota={`${numero(r.total)} inmobiliarias`} />
          <div className={s.filas}>
            {embudo.map((x) => (
              <StatRow key={x.etiqueta} label={x.etiqueta} value={numero(x.n)} share={calificadas ? (x.n / calificadas) * 100 : 0} />
            ))}
          </div>
          <div className={s.pie}>
            <div className={s.pieFila}>
              <Text as="span" size="sm" weight="semibold" tone="strong">
                Descartadas
              </Text>
              <Text as="span" size="sm" mono weight="semibold" tone="strong">
                {numero(descartadas)}
              </Text>
            </div>
            <Text size="xs" tone="muted">
              {r.descartes.map((d) => `${numero(d.n)} ${motivoDescarte(d.motivo).toLowerCase()}`).join(" · ") || "Ninguna."}
              {bajas ? ` · ${numero(bajas)} pidieron la baja` : ""}
            </Text>
          </div>
        </Card>

        <Card padding="md" radius="panel">
          <Cabecera titulo="Calificadas por puntaje" />
          <div className={s.histograma} role="img" aria-label={r.histograma.map((h) => `${h.desde} a ${h.desde + 9}: ${h.n}`).join(", ")}>
            {r.histograma.map((h) => (
              <div key={h.desde} className={s.columna}>
                <span className={s.valor}>{numero(h.n)}</span>
                <span className={s.barraH} data-entra={h.desde >= r.minimoWhatsapp} style={{ height: `${Math.max(2, (h.n / maxHist) * 140)}px` }} />
                <span className={s.eje}>{h.desde}</span>
              </div>
            ))}
          </div>
          <div className={s.leyenda}>
            <span className={s.clave}>
              <span className={s.muestra} data-entra="true" />
              {r.minimoWhatsapp} o más: entran a la cola
            </span>
            <span className={s.clave}>
              <span className={s.muestra} />
              Menos de {r.minimoWhatsapp}
            </span>
          </div>
        </Card>

        <Card padding="md" radius="panel">
          <Cabecera
            titulo="Costo de Google Places"
            extra={
              r.places.facturadoUsd > 0 ? (
                <Badge tone="warning" size="sm" dot>
                  Fuera del cupo
                </Badge>
              ) : (
                <Badge tone="success" size="sm" dot>
                  Dentro del cupo gratis
                </Badge>
              )
            }
          />
          <div className={s.costo}>
            <span className={s.grande}>{usd(r.places.facturadoUsd)}</span>
            <Text size="xs" tone="muted">
              Facturado este mes. Sin el cupo gratis serían {usd(r.places.costoUsd)}.
            </Text>
          </div>
          <div className={s.cupo}>
            <div className={s.pieFila}>
              <Text as="span" size="xs">
                Requests del mes
              </Text>
              <Text as="span" size="xs" mono weight="semibold" tone="strong">
                {numero(r.places.requests)} de {numero(r.places.cupoGratis)}
              </Text>
            </div>
            <Progress value={Math.min(100, (r.places.requests / r.places.cupoGratis) * 100)} label="Cupo gratis usado" />
          </div>
          <p className={s.nota}>Una pasada completa del AMBA usa hasta 300 requests. Fuera del cupo, cada 1.000 cuestan US$ 35.</p>
        </Card>
      </div>

      <div className={s.dos}>
        <Card padding="md" radius="panel">
          <Cabecera titulo="Inmobiliarias por zona" nota={`${r.zonas.recorridas} de ${r.zonas.total} zonas recorridas`} />
          <div className={s.filas}>
            {r.zonas.top.map((z) => (
              <StatRow key={z.id} label={z.nombre} value={numero(z.n)} share={(z.n / maxZona) * 100} />
            ))}
          </div>
          <div className={s.pieFila}>
            <Text as="span" size="xs" tone="muted">
              Las {r.zonas.top.length} zonas con más resultados.
            </Text>
            <Button size="sm" variant="ghost" iconAfter="chevron-right" as={Enlace} href="/zonas">
              Ver todas las zonas
            </Button>
          </div>
        </Card>
        <Card padding="md" radius="panel">
          <Cabecera titulo="Con qué están hechos sus sitios" nota={`${numero(sitiosLeidos)} sitios leídos`} />
          <div className={s.filas}>
            {proveedores.map((p) => (
              <StatRow key={p.proveedor ?? "nada"} label={proveedor(p.proveedor)} value={numero(p.n)} share={(p.n / maxProv) * 100} color={p.proveedor ? undefined : "var(--gray-300)"} />
            ))}
          </div>
          {tokko ? (
            <p className={s.destacado}>
              Tokko aparece en el {porcentaje(tokko, sitiosLeidos)} de los sitios leídos: es el primer candidato para importar propiedades.
            </p>
          ) : null}
        </Card>
      </div>

      <EmbudoWhatsapp />

      <RendimientoPorPlantilla filas={r.plantillas} />

      <SeguimientoAutomatico filas={r.seguimientos} />

      <Card padding="md" radius="panel">
        <Cabecera
          titulo="Mensajes y respuestas por día"
          extra={
            <SegmentedControl
              label="Período"
              size="sm"
              value={dias}
              onChange={setDias}
              options={[
                { value: "7", label: "7 días" },
                { value: "30", label: "30 días" },
                { value: "90", label: "90 días" },
              ]}
            />
          }
        />
        {hayActividad ? (
          <BarChart
            tz={TZ}
            alto={220}
            titulo="Mensajes y respuestas por día"
            descripcion={`Enviados, respuestas humanas y automáticas de los últimos ${dias} días`}
            series={[
              { nombre: "Enviados", color: "accent", puntos: r.serie.map((x) => ({ fecha: x.dia, valor: x.enviados })) },
              { nombre: "Respuestas humanas", color: "accent-2", puntos: r.serie.map((x) => ({ fecha: x.dia, valor: x.respuestas })) },
              { nombre: "Automáticas", color: "muted", puntos: r.serie.map((x) => ({ fecha: x.dia, valor: x.autorespuestas })) },
            ]}
          />
        ) : (
          <EmptyState
            icon="send"
            title="Todavía no salió ningún mensaje"
            tone="plain"
            action={
              <Button variant="soft" icon="whatsapp" as={Enlace} href="/cola">
                Ir a la cola
              </Button>
            }
          >
            Los envíos, las respuestas de una persona y las automáticas de cada día aparecen acá desde el primer WhatsApp.
          </EmptyState>
        )}
      </Card>
    </>
  );
}

/* El A/B de aperturas: una fila por plantilla con la que salió el primer WhatsApp. Las tasas
 * son sobre enviados; el cash es lo cargado a mano en cada cliente. */
function RendimientoPorPlantilla({ filas }: { filas: RendimientoPlantilla[] }) {
  const total = filas.reduce((a, f) => a + f.enviados, 0);
  const cerraron = filas.reduce((a, f) => a + f.cerraron, 0);
  const cobrado = filas.reduce((a, f) => a + f.cobradoUsd, 0);
  const columnas: DataTableColumn<RendimientoPlantilla>[] = [
    {
      key: "plantilla",
      header: "Plantilla",
      width: "26%",
      render: (f) => (
        <Text as="span" size="sm" weight="semibold" tone={f.plantilla ? "strong" : "muted"} mono={Boolean(f.plantilla)}>
          {nombrePlantilla(f.plantilla)}
        </Text>
      ),
    },
    { key: "enviados", header: "Enviados", align: "right", mono: true, render: (f) => numero(f.enviados) },
    {
      key: "respondieron",
      header: "Respuesta humana",
      align: "right",
      mono: true,
      render: (f) => conTasa(f.respondieron, f.tasaRespuesta),
    },
    {
      key: "automaticas",
      header: "Automáticas",
      align: "right",
      mono: true,
      render: (f) => conTasa(f.autorespuestas, f.tasaAutorespuesta),
    },
    { key: "seguimiento", header: "En seguimiento", align: "right", mono: true, render: (f) => numero(f.enSeguimiento) },
    {
      key: "cerraron",
      header: "Cerraron",
      align: "right",
      mono: true,
      render: (f) => (
        <>
          {numero(f.cerraron)}{" "}
          <Text as="span" size="xs" tone="muted" mono>
            {Math.round(f.tasaCierre * 100)}%
          </Text>
        </>
      ),
    },
    { key: "cash", header: "Cash collected", align: "right", mono: true, render: (f) => usd(f.cobradoUsd) },
    { key: "promedio", header: "Promedio por cliente", align: "right", mono: true, hideBelow: "md", render: (f) => (f.cashPromedioUsd === null ? "–" : usd(f.cashPromedioUsd)) },
  ];
  return (
    <Card padding="md" radius="panel">
      <Cabecera
        titulo="Rendimiento por plantilla"
        nota={total ? `${numero(total)} enviados · ${numero(cerraron)} cerraron · ${usd(cobrado)} cobrados` : "Desde el primer envío"}
      />
      {filas.length ? (
        <>
          <DataTable columns={columnas} rows={filas} rowKey={(f) => f.plantilla ?? "sin-plantilla"} variant="flush" minWidth="860px" />
          <p className={s.nota}>
            Cada apertura enviada cuenta para la plantilla con la que salió; el seguimiento automático va aparte. Respuesta humana: contestó una persona después del envío; el porcentaje es esa tasa. Automáticas: el bot de WhatsApp Business contestó solo y no entra en la tasa. En seguimiento: hay respuesta humana, una fase abierta o reunión, y sigue abierta. Cerraron: es cliente. El cash es el total cargado en cada cliente, en dólares. “Sin plantilla” son envíos a mano o en texto libre.
          </p>
        </>
      ) : (
        <EmptyState icon="file-text" title="Todavía no salió ningún WhatsApp con plantilla" tone="plain">
          Cuando mandes desde la cola, acá se compara cuántos responden y cierran con cada apertura.
        </EmptyState>
      )}
    </Card>
  );
}

/* El día 3 y el día 7: cuántos contestan y qué dicen. Cada respuesta queda en el último
 * seguimiento que recibió la inmobiliaria, así el día 7 no se lleva lo que provocó el día 3. */
function SeguimientoAutomatico({ filas }: { filas: RendimientoSeguimiento[] }) {
  const total = filas.reduce((a, f) => a + f.enviados, 0);
  const columnas: DataTableColumn<RendimientoSeguimiento>[] = [
    {
      key: "plantilla",
      header: "Seguimiento",
      width: "26%",
      render: (f) => (
        <span className={s.dia}>
          <Text as="span" size="sm" weight="semibold" tone="strong">
            Día {f.dias}
          </Text>
          <Text as="span" size="xs" tone="muted" mono>
            {f.plantilla}
          </Text>
        </span>
      ),
    },
    { key: "enviados", header: "Enviados", align: "right", mono: true, render: (f) => numero(f.enviados) },
    { key: "respondieron", header: "Respuesta humana", align: "right", mono: true, render: (f) => conTasa(f.respondieron, f.tasaRespuesta) },
    { key: "automaticas", header: "Automáticas", align: "right", mono: true, render: (f) => conTasa(f.autorespuestas, f.tasaAutorespuesta) },
    { key: "intereso", header: "Les interesó", align: "right", mono: true, render: (f) => conTasa(f.leIntereso, f.tasaInteres) },
    { key: "noInteresa", header: "No les interesa", align: "right", mono: true, render: (f) => conTasa(f.noInteresa, f.tasaNoInteresa) },
  ];
  return (
    <Card padding="md" radius="panel">
      <Cabecera titulo="Seguimiento automático" nota="Desde el primer envío" />
      {total ? (
        <>
          <DataTable columns={columnas} rows={filas} rowKey={(f) => f.plantilla} variant="flush" minWidth="640px" />
          <p className={s.nota}>
            Cada respuesta cuenta para el último seguimiento que recibió la inmobiliaria. Las tasas son sobre enviados. Les interesó: tocó el botón de sí, pidió la demo o se marcó en el Pipeline. Los envíos de los últimos días todavía pueden recibir respuesta.
          </p>
        </>
      ) : (
        <EmptyState icon="send" title="Todavía no salió ningún seguimiento" tone="plain">
          Cuando corra el seguimiento del día 3 y del día 7, acá se ve cuántos responden y qué dicen.
        </EmptyState>
      )}
    </Card>
  );
}

/* El embudo del primer WhatsApp por fases, para ver en qué paso se cae la gente: si contestan
 * poco, falla la apertura; si contestan pero no se interesan o la info no convence, falla cómo
 * se presenta Klavia. Toma los envíos del período y mira hasta dónde llegó cada inmobiliaria. */
type PeriodoEmbudo = "todo" | "30" | "7";

function EmbudoWhatsapp() {
  const [periodo, setPeriodo] = useState<PeriodoEmbudo>("todo");
  const consulta = periodo === "todo" ? "" : `?dias=${periodo}`;
  const { datos: m, error, recargar } = useDatos<MetricasEmbudo>(`/api/metricas/embudo${consulta}`);
  useAlCambiar(recargar);
  const desde = periodo === "todo" ? "Desde el primer envío" : `Envíos de los últimos ${periodo} días`;

  return (
    <>
      <Card padding="md" radius="panel">
        <Cabecera
          titulo="Embudo del primer WhatsApp"
          extra={
            <div className={s.controles}>
              <SegmentedControl
                label="Envíos"
                size="sm"
                value={periodo}
                onChange={setPeriodo}
                options={[
                  { value: "todo", label: "Todo" },
                  { value: "30", label: "30 días" },
                  { value: "7", label: "7 días" },
                ]}
              />
              <Button size="sm" variant="outline" icon="download" href={`/api/metricas/embudo.csv${consulta}`}>
                CSV
              </Button>
            </div>
          }
        />
        {!m ? (
          <Carga error={error} onReintentar={recargar} filas={4} />
        ) : !m.total.enviados ? (
          <EmptyState icon="send" title="Todavía no salió ningún primer WhatsApp en este período" tone="plain">
            Cuando se manden aperturas desde la cola, acá se ve en qué fase queda cada inmobiliaria.
          </EmptyState>
        ) : (
          <FasesEmbudo e={m.total} desde={desde} />
        )}
      </Card>
      {m && m.total.enviados ? (
        <>
          <EmbudoPorPlantilla filas={m.porPlantilla} desde={desde} />
          <EmbudoPorDia filas={m.porDia} desde={desde} />
        </>
      ) : null}
    </>
  );
}

function FasesEmbudo({ e, desde }: { e: Embudo; desde: string }) {
  const pct = (x: number) => `${Math.round(x * 100)}%`;
  const fases = [
    { etiqueta: "Mensaje inicial enviado", n: e.enviados, detalle: desde },
    { etiqueta: "Respondieron (persona)", n: e.respondieron, detalle: `${pct(e.tasaRespuesta)} de los enviados` },
    { etiqueta: "Interesados, se les mandó la info", n: e.interesados, detalle: `${pct(e.tasaInteres)} de los que respondieron` },
    { etiqueta: "Les interesó la info", n: e.leInteresoLaInfo, detalle: `${pct(e.tasaInfo)} de los que recibieron la info` },
    { etiqueta: "Reunión", n: e.reunion, detalle: `${pct(e.tasaReunion)} de los que les interesó la info` },
    { etiqueta: "Clientes", n: e.cliente, detalle: `${pct(e.tasaCierre)} de los enviados` },
  ];
  return (
    <>
      <div className={s.tasas}>
        <MetricCard tone="sunken" label="Respuesta humana al mensaje inicial" value={pct(e.tasaRespuesta)} period={`${numero(e.respondieron)} de ${numero(e.enviados)} enviados`} />
        <MetricCard tone="sunken" label="Interés al responder" value={pct(e.tasaInteres)} period={`${numero(e.interesados)} de ${numero(e.respondieron)} que respondieron`} />
        <MetricCard tone="sunken" label="Les interesó la info" value={pct(e.tasaInfo)} period={`${numero(e.leInteresoLaInfo)} de ${numero(e.interesados)} que la recibieron`} />
      </div>
      <div className={s.filas}>
        {fases.map((f) => (
          <StatRow key={f.etiqueta} label={`${f.etiqueta} · ${f.detalle}`} value={numero(f.n)} share={e.enviados ? (f.n / e.enviados) * 100 : 0} />
        ))}
      </div>
      <div className={s.pie}>
        <Text size="xs" tone="muted">
          De los que respondieron: {numero(e.noInteresaDeEntrada)} no les interesa
          {e.sinClasificar ? ` · ${numero(e.sinClasificar)} sin clasificar todavía` : ""}. De los que recibieron la info: {numero(e.noInteresaTrasInfo)} no les interesa · {numero(e.sinRespuestaALaInfo)} sin respuesta.
          {e.autorespuestas ? ` ${numero(e.autorespuestas)} recibieron el aviso automático de WhatsApp Business: no suman como respuesta.` : ""}
        </Text>
      </div>
      <p className={s.nota}>
        Cada inmobiliaria cuenta una vez, en el día y con la plantilla de su primer WhatsApp, y llega hasta la última fase que se registró. Respondieron es una persona: el bot de WhatsApp Business no cuenta ni mueve el Pipeline. Las respuestas humanas entran solas desde Chatwoot; el interés, la info y lo
        que pasó después se marcan moviendo cada inmobiliaria en el Pipeline. Una fase posterior cuenta las anteriores: quien tiene reunión también recibió la info.
      </p>
    </>
  );
}

const conTasa = (n: number, t: number) => (
  <>
    {numero(n)}{" "}
    <Text as="span" size="xs" tone="muted" mono>
      {Math.round(t * 100)}%
    </Text>
  </>
);

function EmbudoPorPlantilla({ filas, desde }: { filas: MetricasEmbudo["porPlantilla"]; desde: string }) {
  type Fila = MetricasEmbudo["porPlantilla"][number];
  const columnas: DataTableColumn<Fila>[] = [
    {
      key: "plantilla",
      header: "Plantilla",
      width: "34%",
      render: (f) => (
        <Text as="span" size="sm" weight="semibold" tone={f.plantilla ? "strong" : "muted"} mono={Boolean(f.plantilla)} className={s.corte}>
          {nombrePlantilla(f.plantilla)}
        </Text>
      ),
    },
    { key: "enviados", header: "Enviados", align: "right", mono: true, render: (f) => numero(f.enviados) },
    { key: "respondieron", header: "Respuesta humana", align: "right", mono: true, render: (f) => conTasa(f.respondieron, f.tasaRespuesta) },
    { key: "interesados", header: "Interesados", align: "right", mono: true, render: (f) => conTasa(f.interesados, f.tasaInteres) },
    { key: "info", header: "Les interesó", align: "right", mono: true, hideBelow: "md", render: (f) => conTasa(f.leInteresoLaInfo, f.tasaInfo) },
  ];
  return (
    <Card padding="md" radius="panel">
      <Cabecera titulo="Fases por plantilla" nota={desde} />
      <DataTable columns={columnas} rows={filas} rowKey={(f) => f.plantilla ?? "sin-plantilla"} variant="flush" minWidth="640px" />
      <p className={s.nota}>Cada tasa es sobre el paso anterior: la respuesta humana sobre enviados, interesados sobre los que respondieron, les interesó sobre los que recibieron la info. El aviso automático no entra en la primera tasa.</p>
    </Card>
  );
}

function EmbudoPorDia({ filas, desde }: { filas: MetricasEmbudo["porDia"]; desde: string }) {
  type Fila = MetricasEmbudo["porDia"][number];
  const columnas: DataTableColumn<Fila>[] = [
    {
      key: "dia",
      header: "Día",
      width: "34%",
      render: (f) => (
        <span className={s.dia}>
          <Text as="span" size="sm" weight="semibold" tone="strong">
            {fecha(`${f.dia}T12:00:00-03:00`)}
          </Text>
          {f.plantillas.map((p) => (
            <Text key={p.plantilla ?? ""} as="span" size="xs" tone="muted" className={s.corte}>
              {numero(p.enviados)} · {nombrePlantilla(p.plantilla)}
            </Text>
          ))}
        </span>
      ),
    },
    { key: "enviados", header: "Enviados", align: "right", mono: true, render: (f) => numero(f.enviados) },
    { key: "respondieron", header: "Respuesta humana", align: "right", mono: true, render: (f) => conTasa(f.respondieron, f.tasaRespuesta) },
    { key: "interesados", header: "Interesados", align: "right", mono: true, render: (f) => conTasa(f.interesados, f.tasaInteres) },
    { key: "info", header: "Les interesó", align: "right", mono: true, hideBelow: "md", render: (f) => conTasa(f.leInteresoLaInfo, f.tasaInfo) },
  ];
  return (
    <Card padding="md" radius="panel">
      <Cabecera titulo="Por día de envío" nota={desde} />
      <DataTable columns={columnas} rows={filas} rowKey={(f) => f.dia} variant="flush" minWidth="640px" />
      <p className={s.nota}>Lo que pasó con los mensajes iniciales de cada día. Los envíos de los últimos días todavía pueden recibir respuesta.</p>
    </Card>
  );
}

function Cabecera({ titulo, nota, extra }: { titulo: string; nota?: string; extra?: ReactNode }) {
  return (
    <div className={s.cabecera}>
      <Heading level={2} size="sm">
        {titulo}
      </Heading>
      {extra ??
        (nota ? (
          <Text as="span" size="xs" tone="muted">
            {nota}
          </Text>
        ) : null)}
    </div>
  );
}
